import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  BarChart3,
  FileText,
  Download,
  Search,
  Filter,
  Plus,
  Eye,
  ExternalLink,
  TrendingUp,
  Users,
  Target,
  X,
  Sparkles,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const Informacion = () => {
  const [activeTab, setActiveTab] = useState("reportes");
  const [loading, setLoading] = useState(true);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [reports, setReports] = useState([]);
  const [impactMetrics, setImpactMetrics] = useState([]);
  const [kpiCards, setKpiCards] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, metricsRes, kpisRes] = await Promise.all([
        axios.get(`${API}/reports`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/impact-metrics`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/kpis`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setReports(reportsRes.data || []);
      setImpactMetrics(metricsRes.data || []);
      setKpiCards(kpisRes.data || []);
    } catch (error) {
      // Silently handle expected 404 errors
      if (error.response?.status !== 404) {
        console.error("Error fetching data:", error);
      }
      // Initialize with empty arrays/objects for production
      setReports([]);
      setImpactMetrics([]);
      setKpiCards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAIAnalysis = async () => {
    if (!aiPrompt.trim()) return;
    
    setAnalyzing(true);
    setAiResponse("");
    
    try {
      const response = await axios.post(
        `${API}/ai/analyze`,
        {
          prompt: aiPrompt,
          context: JSON.stringify({
            reports_count: reports?.length || 0,
            impact_metrics: impactMetrics || [],
            kpis: kpiCards || [],
          }),
        },
        { withCredentials: true }
      );
      setAiResponse(response.data.analysis);
    } catch (error) {
      console.error("AI analysis error:", error);
      setAiResponse("Lo siento, hubo un error al procesar tu solicitud. Por favor intenta de nuevo.");
    } finally {
      setAnalyzing(false);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      quarterly: "badge-primary",
      analytics: "badge-success",
      research: "bg-violet-100 text-violet-700",
    };
    return colors[type] || "bg-slate-100 text-slate-600";
  };

  const getTypeLabel = (type) => {
    const labels = {
      quarterly: "Trimestral",
      analytics: "Analítica",
      research: "Investigación",
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="informacion">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Información</h1>
          <p className="text-slate-500">Repositorio de reportes y analíticas de impacto</p>
        </div>
        <button
          onClick={() => setShowAIModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="ai-analysis-btn"
        >
          <Sparkles size={20} />
          Análisis con IA
        </button>
      </div>

      {/* KPI Summary */}
      {kpiCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {kpiCards.map((kpi, index) => (
            <div key={index} className="glass-panel p-6" data-testid={`kpi-${index}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <kpi.icon size={24} className={kpi.color} />
                </div>
                <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
                  <TrendingUp size={16} />
                  {kpi.change}
                </span>
              </div>
              <p className="text-3xl font-bold text-slate-800">{kpi.value}</p>
              <p className="text-slate-500 mt-1">{kpi.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="glass-panel p-1 inline-flex rounded-full">
        {[
          { id: "reportes", label: "Reportes", icon: FileText },
          { id: "analiticas", label: "Analíticas", icon: BarChart3 },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? "bg-primary text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Reportes Tab */}
      {activeTab === "reportes" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar reportes..."
                className="w-full glass-input pl-10"
                data-testid="search-reports-input"
              />
            </div>
            <button className="btn-secondary flex items-center gap-2">
              <Filter size={18} />
              Filtrar
            </button>
          </div>

          {reports.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reports.map((report) => (
              <div
                key={report.id}
                className="glass-panel p-6 card-hover"
                data-testid={`report-${report.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText size={28} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-slate-800">{report.title}</h3>
                      <span className={`badge text-xs ${getTypeColor(report.type)}`}>
                        {getTypeLabel(report.type)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                      {report.description}
                    </p>
                    <div className="flex items-center gap-4 mt-4 text-sm text-slate-400">
                      <span>
                        {new Date(report.date).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                      <span>{report.downloads} descargas</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end gap-2">
                  <button className="btn-ghost text-sm flex items-center gap-1">
                    <Eye size={16} />
                    Ver
                  </button>
                  <button className="btn-ghost text-sm flex items-center gap-1">
                    <Download size={16} />
                    Descargar
                  </button>
                </div>
              </div>
              ))}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <FileText size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay reportes disponibles</p>
            </div>
          )}
        </div>
      )}

      {/* Analiticas Tab */}
      {activeTab === "analiticas" && (
        <div className="space-y-6">
          {impactMetrics.length > 0 ? (
            <>
              <div className="glass-panel p-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-6">
                  Tendencia de Impacto (últimos 6 meses)
                </h2>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={impactMetrics}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 12 }} />
                      <YAxis tick={{ fill: "#64748B", fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "none",
                          borderRadius: "12px",
                          boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="students"
                        stroke="#6C8ECC"
                        strokeWidth={3}
                        dot={{ fill: "#6C8ECC", strokeWidth: 2 }}
                        name="Estudiantes"
                      />
                      <Line
                        type="monotone"
                        dataKey="courses"
                        stroke="#10B981"
                        strokeWidth={3}
                        dot={{ fill: "#10B981", strokeWidth: 2 }}
                        name="Cursos"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="glass-panel p-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-6">
                  Comunidades Atendidas por Mes
                </h2>
                <div className="h-64" style={{ minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={impactMetrics}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 12 }} />
                      <YAxis tick={{ fill: "#64748B", fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "none",
                          borderRadius: "12px",
                          boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Bar
                        dataKey="communities"
                        fill="#9EC6EE"
                        radius={[8, 8, 0, 0]}
                        name="Comunidades"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <div className="glass-panel p-12 text-center">
              <BarChart3 size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay datos de analíticas disponibles</p>
            </div>
          )}
        </div>
      )}

      {/* AI Analysis Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-8 w-full max-w-2xl animate-slide-up" data-testid="ai-analysis-modal">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Sparkles size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">Análisis con IA</h2>
              </div>
              <button
                onClick={() => {
                  setShowAIModal(false);
                  setAiResponse("");
                  setAiPrompt("");
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  ¿Qué te gustaría analizar?
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  className="w-full glass-input min-h-[100px]"
                  placeholder="Ej: Analiza las tendencias de crecimiento de estudiantes y sugiere estrategias para mejorar la retención..."
                  data-testid="ai-prompt-input"
                />
              </div>

              <button
                onClick={handleAIAnalysis}
                disabled={analyzing || !aiPrompt.trim()}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="run-ai-analysis-btn"
              >
                {analyzing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Analizando...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    Analizar
                  </>
                )}
              </button>

              {aiResponse && (
                <div className="mt-6 p-4 rounded-2xl bg-slate-50">
                  <h3 className="font-semibold text-slate-800 mb-2">Resultado del análisis:</h3>
                  <div className="prose prose-slate prose-sm max-w-none">
                    <p className="text-slate-600 whitespace-pre-wrap">{aiResponse}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Informacion;
